import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Paper,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { generatePDFFromElement } from '../utils/pdfGenerator';
import Header from '../components/Header';
import RichTextEditor from '../components/RichTextEditor';
import AttachmentViewer from '../components/AttachmentViewer';
import { API_BASE_URL, API_ENDPOINTS } from '../config/apiClient';
import { useAuth } from '../context/AuthContext';
import { APP_CONFIG, extractRoleFromApprovalString } from '../config/appConfig';

interface ExpenseDetailsData {
  id: number;
  documentId: string;
  title: string;
  quantity?: number;
  unitPrice?: number;
  totalPrice?: number;
  totalAmount?: number;
  expenseItems?: Array<{
    id: string;
    dateOfExpense: string;
    item: string;
    rate: number;
    quantity: number;
    total?: number;
    comment: string;
  }>;
  comment: string;
  isExceptionRequest: boolean;
  sendExceptionApprovalTo: string;
  ExceptionApproval: any;
  budgetWithinApprovalLevel: string;
  attachment: any[];
  ApprovedItem: {
    id: number;
    documentId: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    remianingAmount: number;
    item_estimate: {
      id: number;
      documentId: string;
      item: string;
      totalCost: number;
    };
    aproval_request: {
      id: number;
      documentId: string;
      approvalStatus: string;
      title: string;
      approvals: Array<{
        email: string;
        username: string;
        status: string;
        approvalDate?: string;
        comments?: string;
      }>;
    };
  };
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

const ExpenseDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, user, handleUnauthorized } = useAuth();
  const [expense, setExpense] = useState<ExpenseDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState<number | null>(null);
  const [attachmentViewerOpen, setAttachmentViewerOpen] = useState(false);
  const [relatedExpenses, setRelatedExpenses] = useState<any[]>([]);
  const [approvingException, setApprovingException] = useState(false);
  const [exceptionApprovalComments, setExceptionApprovalComments] = useState('');
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Helper function to get expense total amount - handles both new (expenseItems/totalAmount) and old (totalPrice) formats
  const getExpenseTotalAmount = (exp: ExpenseDetailsData | null): number => {
    if (!exp) return 0;
    // New format: calculate from expenseItems if available
    if (exp.expenseItems && exp.expenseItems.length > 0) {
      return exp.expenseItems.reduce((sum, item) => {
        const itemCost = item.total !== undefined ? item.total : item.rate * item.quantity;
        return sum + itemCost;
      }, 0);
    }
    // New format: use totalAmount field if no items
    if (exp.totalAmount !== null && exp.totalAmount !== undefined) {
      return exp.totalAmount;
    }
    // Old format: totalPrice field
    if (exp.totalPrice !== null && exp.totalPrice !== undefined) {
      return exp.totalPrice;
    }
    // Fallback
    return 0;
  };

  const getExpenseUnitPrice = (exp: ExpenseDetailsData | null): number => {
    if (!exp) return 0;
    return exp.unitPrice || 0;
  };

  useEffect(() => {
    const fetchExpenseDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use numbered populate parameters like ExpenseReport does
        const endpoint = `${API_BASE_URL}${API_ENDPOINTS.EXPENSES.GET(id!)}?populate[0]=attachment&populate[1]=ApprovedItem&populate[2]=ApprovedItem.item_estimate&populate[3]=ApprovedItem.aproval_request`;

        const response = await fetch(
          endpoint,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            handleUnauthorized();
            return;
          }
          throw new Error(`Failed to fetch expense details: ${response.statusText}`);
        }

        const data = await response.json();
        setExpense(data.data);

        // Fetch related expenses for the same ApprovedItem
        if (data.data?.ApprovedItem?.documentId) {
          try {
            const relatedEndpoint = `${API_BASE_URL}${API_ENDPOINTS.EXPENSES.LIST}?pagination[limit]=100&filters[ApprovedItem][documentId][$eq]=${data.data.ApprovedItem.documentId}&populate[0]=ApprovedItem&populate[1]=ApprovedItem.item_estimate&sort=createdAt:desc`;
            const relatedResponse = await fetch(relatedEndpoint, {
              headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
              },
            });

            if (relatedResponse.ok) {
              const relatedData = await relatedResponse.json();
              // Filter out the current expense
              const others = relatedData.data.filter((exp: any) => exp.documentId !== id);
              setRelatedExpenses(others);
            }
          } catch (relatedErr) {
            console.warn('Failed to fetch related expenses:', relatedErr);
          }
        }
      } catch (err) {
        console.error('Error fetching expense details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load expense details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchExpenseDetails();
    }
  }, [id, token]);

  const getBudgetStatusColor = (status: string) => {
    switch (status) {
      case 'withinApprovalLevel':
        return '#c8e6c9';
      case 'aboveApprovalLevelButWithinThreshold':
        return '#ffe0b2';
      case 'aboveApprovalLevel':
        return '#ffcdd2';
      default:
        return '#f5f5f5';
    }
  };

  const getBudgetStatusLabel = (status: string) => {
    switch (status) {
      case 'withinApprovalLevel':
        return 'Within Approval Level';
      case 'aboveApprovalLevelButWithinThreshold':
        return 'Above Approval (+20% Threshold)';
      case 'aboveApprovalLevel':
        return 'Above Approval Level';
      default:
        return 'Unknown';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExceptionApproval = async (status: 'Approved' | 'Rejected') => {
    if (!expense) return;

    setApprovingException(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/expenses/${expense.documentId}/exception-approval`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            status,
            comments: exceptionApprovalComments || null,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to process exception approval');
      }

      // Refetch the expense to show updated approval
      const updatedResponse = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.EXPENSES.GET(id!)}?populate[0]=attachment&populate[1]=ApprovedItem&populate[2]=ApprovedItem.item_estimate&populate[3]=ApprovedItem.aproval_request`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (updatedResponse.ok) {
        const updatedData = await updatedResponse.json();
        setExpense(updatedData.data);
      }

      alert(`Exception ${status.toLowerCase()} successfully!`);
    } catch (err) {
      console.error('Error processing exception approval:', err);
      alert(err instanceof Error ? err.message : 'Failed to process approval');
    } finally {
      setApprovingException(false);
    }
  };

  const isDirectorOrSecretary = (): boolean => {
    if (!expense || !user) return false;
    
    const userEmail = user.email?.toLowerCase() || '';
    const directorEmail = APP_CONFIG.DIRECTOR_EMAIL.toLowerCase();
    const secretaryEmail = APP_CONFIG.SECRETARY_EMAIL.toLowerCase();
    
    return userEmail === directorEmail || userEmail === secretaryEmail;
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloadingPDF(true);
      const timestamp = new Date().toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).replace(/[/:]/g, '-');
      
      const filename = `Expense_Report_${expense?.title?.replace(/\s+/g, '_') || 'Details'}_${timestamp}.pdf`;
      
      // Collect all image attachments to include in the PDF
      const imageAttachments: any[] = [];
      if (expense?.attachment && expense.attachment.length > 0) {
        expense.attachment.forEach(file => {
          // Include only image files as they will be embedded in the PDF
          if (file.mime?.startsWith('image/')) {
            imageAttachments.push({
              name: file.name,
              url: file.url,
              type: file.mime,
              size: file.size || 0,
            });
          }
        });
      }
      
      await generatePDFFromElement('expense-pdf-content', filename, {
        orientation: 'portrait',
        format: 'a4',
        margin: 10,
      }, imageAttachments);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const canApproveException = (): boolean => {
    if (!expense || !expense.isExceptionRequest) {
      console.log('DEBUG: Not an exception request or no expense');
      return false;
    }
    // Don't show approval buttons if already approved or rejected
    if (expense.ExceptionApproval && (expense.ExceptionApproval.status === 'Approved' || expense.ExceptionApproval.status === 'Rejected')) {
      console.log('DEBUG: Already approved/rejected');
      return false;
    }
    
    // Check if user is director or secretary
    if (isDirectorOrSecretary()) {
      console.log('DEBUG: User is director or secretary');
      return true;
    }
    
    // Check if user is the person the exception was sent to
    if (expense.sendExceptionApprovalTo && user?.email) {
      const userEmail = user.email.toLowerCase();
      const directorEmail = APP_CONFIG.DIRECTOR_EMAIL.toLowerCase();
      const secretaryEmail = APP_CONFIG.SECRETARY_EMAIL.toLowerCase();
      
      console.log('DEBUG canApproveException:');
      console.log('  userEmail:', userEmail);
      console.log('  directorEmail:', directorEmail);
      console.log('  secretaryEmail:', secretaryEmail);
      console.log('  sendExceptionApprovalTo:', expense.sendExceptionApprovalTo);
      
      // Extract role from sendExceptionApprovalTo (format: "President [Name]" or "Secretary [Name]")
      const role = extractRoleFromApprovalString(expense.sendExceptionApprovalTo);
      
      if (role === 'President' && userEmail === directorEmail) {
        console.log('DEBUG: User can approve (President)');
        return true;
      }
      if (role === 'Secretary' && userEmail === secretaryEmail) {
        console.log('DEBUG: User can approve (Secretary)');
        return true;
      }
    } else {
      console.log('DEBUG: No sendExceptionApprovalTo or user email');
      console.log('  sendExceptionApprovalTo:', expense.sendExceptionApprovalTo);
      console.log('  user?.email:', user?.email);
    }
    
    console.log('DEBUG: User cannot approve exception');
    return false;
  };

  // Debug effect to check approval eligibility
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (expense) {
      console.log('DEBUG useEffect - Approval eligibility:');
      console.log('  isExceptionRequest:', expense.isExceptionRequest);
      console.log('  canApproveException():', canApproveException());
      console.log('  user:', user);
      console.log('  expense.sendExceptionApprovalTo:', expense.sendExceptionApprovalTo);
    }
  }, [expense, user]);

  if (loading) {
    return (
      <Box>
        <Header />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading expense details...</Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  if (error || !expense) {
    return (
      <Box>
        <Header />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">{error || 'Expense not found'}</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Container>
      </Box>
    );
  }

  const approvedItem = expense.ApprovedItem;
  const approvalRequest = approvedItem?.aproval_request;
  const itemEstimate = approvedItem?.item_estimate;

  return (
    <Box>
      <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Back Button and Download Button */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Button
            startIcon={<FileDownloadIcon />}
            variant="contained"
            sx={{ backgroundColor: '#1976d2' }}
            onClick={handleDownloadPDF}
            disabled={downloadingPDF}
          >
            {downloadingPDF ? 'Generating PDF...' : 'Download Report'}
          </Button>
        </Box>

        {/* Exception Approval Section */}
        {expense.isExceptionRequest && canApproveException() && (
          <Card sx={{ mb: 3, backgroundColor: '#fff3cd', borderLeft: '5px solid #ff9800' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#ff6b6b' }}>
                ⚠️ Exception Request Approval Required
              </Typography>
              <Typography sx={{ mb: 2, fontSize: '0.9rem' }}>
                This is an exception request for <strong>{expense.sendExceptionApprovalTo}</strong>. 
                Please review and approve or reject this request below.
              </Typography>

              <TextField
                multiline
                rows={3}
                fullWidth
                placeholder="Optional: Add comments for your decision"
                value={exceptionApprovalComments}
                onChange={(e) => setExceptionApprovalComments(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                  onClick={() => handleExceptionApproval('Approved')}
                  disabled={approvingException}
                >
                  ✓ Approve
                </Button>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#f44336', '&:hover': { backgroundColor: '#da190b' } }}
                  onClick={() => handleExceptionApproval('Rejected')}
                  disabled={approvingException}
                >
                  ✕ Reject
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Exception Approval Status - Show if already approved/rejected */}
        {expense.isExceptionRequest && expense.ExceptionApproval && (expense.ExceptionApproval.status === 'Approved' || expense.ExceptionApproval.status === 'Rejected') && (
          <Card sx={{ mb: 3, backgroundColor: expense.ExceptionApproval.status === 'Approved' ? '#c8e6c9' : '#ffcdd2', borderLeft: `5px solid ${expense.ExceptionApproval.status === 'Approved' ? '#4caf50' : '#f44336'}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {expense.ExceptionApproval.status === 'Approved' ? '✓ Approved' : '✕ Rejected'}
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', mb: 1 }}>
                <strong>Approved By:</strong> {expense.ExceptionApproval.approvedBy} ({expense.ExceptionApproval.recipientRole})
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', mb: 1 }}>
                <strong>Date:</strong> {formatDate(expense.ExceptionApproval.approvalDate)}
              </Typography>
              {expense.ExceptionApproval.comments && (
                <Typography sx={{ fontSize: '0.9rem', mt: 2, fontStyle: 'italic' }}>
                  <strong>Comments:</strong> {expense.ExceptionApproval.comments}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Expense Details Card */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                backgroundColor: getBudgetStatusColor(expense.budgetWithinApprovalLevel),
                transition: 'all 0.3s ease'
              }}
            >
              <CardContent>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {expense.title}
                </Typography>

                {/* Budget Status Badge */}
                <Box sx={{ mb: 3, p: 1.5, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                    Budget Status: {getBudgetStatusLabel(expense.budgetWithinApprovalLevel)}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Expense Details */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {(expense.quantity !== null && expense.quantity !== undefined) && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                        Quantity
                      </Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        {expense.quantity}
                      </Typography>
                    </Box>
                  </Grid>
                  )}

                  {getExpenseUnitPrice(expense) > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                        Unit Price
                      </Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        ₹{getExpenseUnitPrice(expense).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                        Spent Amount
                      </Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1976d2' }}>
                        ₹{getExpenseTotalAmount(expense).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Approved Amount - Only for exception requests after approval/rejection */}
                  {expense.isExceptionRequest && expense.ExceptionApproval && (expense.ExceptionApproval.status === 'Approved' || expense.ExceptionApproval.status === 'Rejected') && (
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                          Approved Amount
                        </Typography>
                        <Typography 
                          sx={{ 
                            fontSize: '1rem', 
                            fontWeight: 600,
                            color: expense.ExceptionApproval.status === 'Approved' ? '#388e3c' : '#d32f2f'
                          }}
                        >
                          ₹{expense.ExceptionApproval.status === 'Approved' ? getExpenseTotalAmount(expense).toFixed(2) : '0.00'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                        Remaining Budget
                      </Typography>
                      <Typography 
                        sx={{ 
                          fontSize: '1rem', 
                          fontWeight: 600,
                          color: (approvedItem?.remianingAmount || 0) < 0 ? '#d32f2f' : '#388e3c'
                        }}
                      >
                        ₹{(approvedItem?.remianingAmount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Expense Items Table - for new format expenses */}
                {expense.expenseItems && expense.expenseItems.length > 0 && (
                  <>
                    <Divider sx={{ mb: 2, mt: 2 }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 1.5 }}>
                        Expense Items Details
                      </Typography>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Item</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Rate</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Quantity</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Total</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Comment</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {expense.expenseItems.map((item: any, index: number) => (
                              <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                <TableCell sx={{ fontSize: '0.75rem' }}>{item.dateOfExpense}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem' }}>{item.item}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.75rem' }}>₹{(item.rate || 0).toFixed(2)}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{(item.quantity || 0).toFixed(2)}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1976d2' }}>
                                  ₹{((item.total !== undefined ? item.total : item.rate * item.quantity) || 0).toFixed(2)}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.75rem' }}>{item.comment || '-'}</TableCell>
                              </TableRow>
                            ))}
                            {expense.expenseItems.length > 0 && (
                              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell colSpan={4} align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                  Grand Total:
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1976d2' }}>
                                  ₹{getExpenseTotalAmount(expense).toFixed(2)}
                                </TableCell>
                                <TableCell />
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>
                  </>
                )}

                {/* Comment */}
                {expense.comment && (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                        Comment
                      </Typography>
                      <RichTextEditor
                        value={expense.comment}
                        onChange={() => {}}
                        readOnly={true}
                        minHeight="auto"
                      />
                    </Box>
                  </>
                )}

                {/* Exception Request */}
                {expense.isExceptionRequest && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#d32f2f', mb: 1 }}>
                        ⚠ EXCEPTION REQUEST
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                        <strong>Sent To:</strong> {expense.sendExceptionApprovalTo}
                      </Typography>
                      {expense.ExceptionApproval && (
                        <Typography sx={{ fontSize: '0.875rem' }}>
                          <strong>Status:</strong> {expense.ExceptionApproval.status || 'Pending'}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}

                {/* Attachments */}
                {expense.attachment && expense.attachment.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 1 }}>
                        Attachments ({expense.attachment.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {expense.attachment.map((file: any, index: number) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              cursor: 'pointer',
                              width: 100,
                              '&:hover': {
                                transform: 'scale(1.05)',
                              },
                              transition: 'transform 0.2s',
                            }}
                            onClick={() => {
                              setSelectedAttachmentIndex(index);
                              setAttachmentViewerOpen(true);
                            }}
                          >
                            {file.mime?.startsWith('image/') ? (
                              <Box
                                component="img"
                                src={`${API_BASE_URL}${file.url}`}
                                alt={file.name}
                                sx={{
                                  width: 70,
                                  height: 70,
                                  borderRadius: 1,
                                  objectFit: 'cover',
                                  border: '1px solid #ddd',
                                  '&:hover': { opacity: 0.8 }
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 70,
                                  height: 70,
                                  borderRadius: 1,
                                  backgroundColor: file.mime === 'application/pdf' ? '#f44336' : '#e0e0e0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  color: file.mime === 'application/pdf' ? '#fff' : '#666',
                                  '&:hover': { opacity: 0.8 }
                                }}
                              >
                                {file.ext?.toUpperCase().slice(1) || 'FILE'}
                              </Box>
                            )}
                            <Typography 
                              sx={{ 
                                fontSize: '0.6rem', 
                                mt: 1, 
                                textAlign: 'center', 
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                wordBreak: 'break-word',
                              }}
                              title={file.name}
                            >
                              {file.name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </>
                )}

                {/* Metadata */}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>
                    <strong>Created:</strong> {formatDate(expense.createdAt)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>
                    <strong>Updated:</strong> {formatDate(expense.updatedAt)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>
                    <strong>Published:</strong> {formatDate(expense.publishedAt)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Previous Expenses Section */}
            {relatedExpenses.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Another Expenses for This Approval Item
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Title</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {relatedExpenses.map((relatedExpense: any) => (
                          <TableRow key={relatedExpense.documentId} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {new Date(relatedExpense.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {relatedExpense.title}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1976d2' }}>
                              ₹{((relatedExpense.totalAmount !== undefined ? relatedExpense.totalAmount : relatedExpense.totalPrice) || 0).toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 0.5,
                                  backgroundColor: 
                                    relatedExpense.budgetWithinApprovalLevel === 'withinApprovalLevel' ? '#c8e6c9' :
                                    relatedExpense.budgetWithinApprovalLevel === 'aboveApprovalLevelButWithinThreshold' ? '#ffe0b2' :
                                    '#ffcdd2',
                                  fontWeight: 600
                                }}
                              >
                                {relatedExpense.budgetWithinApprovalLevel === 'withinApprovalLevel' ? 'Within' :
                                 relatedExpense.budgetWithinApprovalLevel === 'aboveApprovalLevelButWithinThreshold' ? 'Above +20%' :
                                 'Exceeded'}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/expense/${relatedExpense.documentId}`)}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Approval Details Card */}
          <Grid item xs={12} md={4}>
            {approvalRequest && (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Approval Details
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  {/* Approval Request Info with Link */}
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                      Approval Title
                    </Typography>
                    <a
                      href={`/approval/${approvalRequest.documentId}`}
                      style={{
                        color: '#1976d2',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        display: 'inline-block'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      {approvalRequest.title} →
                    </a>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                      Status
                    </Typography>
                    <Typography 
                      sx={{ 
                        fontSize: '0.875rem', 
                        fontWeight: 600,
                        color: approvalRequest.approvalStatus === 'Approved' ? '#388e3c' : '#f57c00'
                      }}
                    >
                      {approvalRequest.approvalStatus}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* Expense Category Section */}
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                    Expense Category
                  </Typography>

                  {/* Item Estimate */}
                  {itemEstimate && (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                          Category Name
                        </Typography>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                          {itemEstimate.item}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
                          Estimated Budget
                        </Typography>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1976d2' }}>
                          ₹{(approvedItem?.totalPrice || 0).toFixed(2)}
                        </Typography>
                      </Box>

                      <Divider sx={{ mb: 2 }} />
                    </>
                  )}

                  {/* Approval Chain */}
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', mb: 1.5 }}>
                    Approval Chain ({approvalRequest.approvals?.length || 0})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {approvalRequest.approvals?.map((approval: any, index: number) => (
                      <Box
                        key={index}
                        sx={{
                          p: 1.5,
                          backgroundColor: approval.status === 'Approved' ? '#f1f8e9' : approval.status === 'Rejected' ? '#ffebee' : '#fff9c4',
                          borderRadius: 1,
                          borderLeft: `4px solid ${
                            approval.status === 'Approved' ? '#388e3c' : 
                            approval.status === 'Rejected' ? '#d32f2f' : 
                            '#f57c00'
                          }`,
                          position: 'relative'
                        }}
                      >
                        {/* Approval Status Indicator */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 0.5 }}>
                          <Box>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 0.25 }}>
                              {approval.username}
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                              {approval.email}
                            </Typography>
                          </Box>
                          <Typography 
                            sx={{ 
                              fontSize: '0.65rem', 
                              fontWeight: 600, 
                              px: 1,
                              py: 0.25,
                              borderRadius: 0.5,
                              backgroundColor: 'rgba(255,255,255,0.6)',
                              color: approval.status === 'Approved' ? '#388e3c' : 
                                     approval.status === 'Rejected' ? '#d32f2f' : 
                                     '#f57c00'
                            }}
                          >
                            {approval.status}
                          </Typography>
                        </Box>

                        {/* Approval Date */}
                        {approval.approvalDate && (
                          <Typography sx={{ fontSize: '0.65rem', color: '#666', mb: 0.5, fontStyle: 'italic' }}>
                            <strong>Date:</strong> {approval.approvalDate}
                          </Typography>
                        )}

                        {/* Approval Comments */}
                        {approval.comments && (
                          <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#333', mb: 0.25 }}>
                              Comment:
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: '#555', fontStyle: 'italic' }}>
                              "{approval.comments}"
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>

        {/* Hidden PDF Content - for download */}
        <div id="expense-pdf-content" className="print-only" style={{ display: 'none' }}>
          <Paper sx={{ p: 4, mb: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {expense?.title || 'Expense Details'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                ID: {expense?.documentId}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Header Information */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                    Expense ID
                  </Typography>
                  <Typography variant="h6">#{expense?.documentId}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                    Budget Status
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {getBudgetStatusLabel(expense?.budgetWithinApprovalLevel || '')}
                  </Typography>
                </Box>
              </Grid>

              {/* Expense Details */}
              {(expense?.quantity !== null && expense?.quantity !== undefined) && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                    Quantity
                  </Typography>
                  <Typography variant="body1">{expense?.quantity}</Typography>
                </Box>
              </Grid>
              )}

              {getExpenseUnitPrice(expense) > 0 && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                    Unit Price
                  </Typography>
                  <Typography variant="body1">₹{getExpenseUnitPrice(expense).toFixed(2)}</Typography>
                </Box>
              </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                    Spent Amount
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                    ₹{getExpenseTotalAmount(expense).toFixed(2)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                    Remaining Budget
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    ₹{(expense?.ApprovedItem?.remianingAmount || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Grid>

              {/* Expense Items Table */}
              {expense?.expenseItems && expense.expenseItems.length > 0 && (
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                      Expense Items Details
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ border: '1px solid #ddd', borderRadius: 1 }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rate</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Comment</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {expense.expenseItems.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{item.dateOfExpense}</TableCell>
                              <TableCell>{item.item}</TableCell>
                              <TableCell align="right">₹{item.rate.toFixed(2)}</TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                ₹{((item.total !== undefined ? item.total : item.rate * item.quantity)).toFixed(2)}
                              </TableCell>
                              <TableCell>{item.comment}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* Comment */}
              {expense?.comment && (
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Comment
                    </Typography>
                    <Box sx={{ p: 1.5, backgroundColor: '#f9f9f9', borderRadius: 1, border: '1px solid #ddd' }}>
                      <div dangerouslySetInnerHTML={{ __html: expense.comment }} />
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* Exception Request Info */}
              {expense?.isExceptionRequest && (
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, backgroundColor: '#fff3cd', borderRadius: 1, borderLeft: '4px solid #ff9800' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 1 }}>
                      ⚠ EXCEPTION REQUEST
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Sent To:</strong> {expense.sendExceptionApprovalTo}
                    </Typography>
                    {expense.ExceptionApproval && (
                      <Typography variant="body2">
                        <strong>Status:</strong> {expense.ExceptionApproval.status || 'Pending'}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Approval Details */}
              {expense?.ApprovedItem?.aproval_request && (
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                      Approval Request Details
                    </Typography>
                    <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Title:</strong> {expense.ApprovedItem.aproval_request.title}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Status:</strong> {expense.ApprovedItem.aproval_request.approvalStatus}
                      </Typography>
                      {expense.ApprovedItem.item_estimate && (
                        <Typography variant="body2">
                          <strong>Category:</strong> {expense.ApprovedItem.item_estimate.item} (₹{expense.ApprovedItem.item_estimate.totalCost.toFixed(2)})
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* Metadata */}
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                    <strong>Created:</strong> {formatDate(expense?.createdAt || '')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                    <strong>Updated:</strong> {formatDate(expense?.updatedAt || '')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    <strong>Published:</strong> {formatDate(expense?.publishedAt || '')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Image Attachments Section */}
            {expense?.attachment && expense.attachment.length > 0 && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '2px solid #ddd' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Attachments ({expense.attachment.length})
                </Typography>
                <Grid container spacing={2}>
                  {expense.attachment.map((file: any, index: number) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box sx={{ p: 1.5, backgroundColor: '#f9f9f9', borderRadius: 1, border: '1px solid #ddd', textAlign: 'center' }}>
                        {file.mime?.startsWith('image/') ? (
                          <>
                            <Box
                              component="img"
                              src={`${API_BASE_URL}${file.url}`}
                              alt={file.name}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                borderRadius: 1,
                                marginBottom: 1,
                                objectFit: 'contain',
                                border: '1px solid #ddd',
                              }}
                            />
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', wordBreak: 'break-word' }}>
                              {file.name}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                              📎 {file.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {file.ext?.toUpperCase()} File
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        </div>

        {/* Attachment Viewer Modal */}
        {expense?.attachment && (
          <AttachmentViewer
            open={attachmentViewerOpen}
            onClose={() => setAttachmentViewerOpen(false)}
            attachments={expense.attachment.map((file: any) => ({
              url: file.url,
              name: file.name,
              mime: file.mime,
              ext: file.ext,
            }))}
            initialIndex={selectedAttachmentIndex || 0}
            apiBaseUrl={API_BASE_URL}
          />
        )}
      </Container>
    </Box>
  );
};

export default ExpenseDetails;
